import { useContext, useEffect } from 'react';
import { Empty, Skeleton } from 'antd';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { useBenefitCoverage, CoveragePlan } from 'all-in-one-shared-api';
import {
  getCommonAccommodationLevel,
  getCommonReimbursement,
  getCurrentPolicy,
  getRelationshipValue,
  isHasDependennts,
} from '../utils/utils';
import BenefitContext from '../contexts/BenefitContext';
import { shouldShowDisclaimer } from './coverageConditions';
import DescriptionSection from './components/DescriptionSection';
import DentalCoverageError from './components/DentalCoverageError';
import CopaymentDisclaimer from './components/CopaymentDisclaimer';
import BenefitGroupSection from './components/BenefitGroupSection';
import BenefitAccordionView from './components/BenefitAccordionView';
import { useTranslation } from 'react-i18next';

export interface CoverageScreenProps {
  coveragePlan: CoveragePlan;
}

const Coverage = ({ coveragePlan }: CoverageScreenProps) => {
  const { ready, token } = useKeycloakReady();
  const { selectedFilterName, selectedFilterPeriod, policyInfoData } = useContext(BenefitContext);
  const { i18n, t } = useTranslation();

  const currentPolicy = getCurrentPolicy(policyInfoData, selectedFilterName, selectedFilterPeriod);

  const memberType = currentPolicy?.memberType ?? '';
  const familyRelation = getRelationshipValue(currentPolicy?.familyRelationship ?? '');
  const renewalNumber = currentPolicy?.renewalNumber ?? -1;

  const shouldShowAccommodationLevel = [CoveragePlan.HOSPITALISATION, CoveragePlan.SUPP_MAJOR_MEDICAL].includes(
    coveragePlan,
  );

  const {
    shouldFetch: shouldFetchCoverage,
    data: coverageData,
    error: coverageError,
    isLoading: coverageIsLoading,
  } = useBenefitCoverage(
    { key: 'getBenefitCoverage', memberType, familyRelation, coveragePlan, renewalNumber },
    token ?? '',
    parseInt(memberType),
    familyRelation,
    coveragePlan,
    renewalNumber,
  );

  useEffect(() => {
    shouldFetchCoverage(
      !!ready && memberType !== undefined && Boolean(familyRelation) && !!coveragePlan && renewalNumber !== -1,
    );
  }, [ready, policyInfoData, memberType, familyRelation, coveragePlan, renewalNumber, shouldFetchCoverage]);

  const benefitItems = coverageData?.benefitItems ?? [];
  const commonAccommodation = getCommonAccommodationLevel(benefitItems, i18n.language);
  const commonReimbursement = getCommonReimbursement(benefitItems);

  if (coverageIsLoading) {
    return <Skeleton active={coverageIsLoading} />;
  }

  if (coverageError && coverageError.code === 'dental.benefit.excluded') {
    return <DentalCoverageError />;
  }

  if (coverageError && coverageError.code === 'benefit.coverage.notfound') {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span>{t('benefits.noData')}</span>} />;
  }

  return (
    <>
      <DescriptionSection
        hasDependents={isHasDependennts(policyInfoData)}
        reimbursementRate={commonReimbursement}
        accommodationLevel={shouldShowAccommodationLevel ? commonAccommodation : ''}
      />
      <BenefitAccordionView
        coveragePlan={coveragePlan}
        benefitItems={benefitItems}
        showItemAccomodation={shouldShowAccommodationLevel && !commonAccommodation}
        showItemReimbursementRate={!commonReimbursement}
      />
      {shouldShowDisclaimer(coveragePlan) && <CopaymentDisclaimer />}
      <BenefitGroupSection benefitGroups={coverageData?.benefitGroups} />
    </>
  );
};

export default Coverage;
